// src/pages/CRM/Finance/InvoiceForm.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, InputAdornment, Divider, Grid, CircularProgress, Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const emptyLineItem = { description: '', quantity: 1, unit_price: 0, amount: 0 };

const InvoiceForm = () => {
  const { chatbotId, invoiceId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = Boolean(invoiceId) && invoiceId !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Contact search
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  });
  const [lineItems, setLineItems] = useState([{ ...emptyLineItem }]);
  const [taxRate, setTaxRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');

  useEffect(() => {
    document.title = isEdit ? 'Edit Invoice | KlonIT CRM' : 'New Invoice | KlonIT CRM';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canModify && isEdit) {
      loadInvoice();
    }
  }, [canModify, invoiceId]);

  const checkPermissions = async () => {
    try {
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadInvoice = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/crm/invoices/${invoiceId}`);
      if (response.data.success) {
        const inv = response.data.data;
        setInvoiceNumber(inv.invoice_number || '');
        setInvoiceDate(inv.invoice_date ? new Date(inv.invoice_date) : new Date());
        setDueDate(inv.due_date ? new Date(inv.due_date) : new Date());
        setSelectedContact(inv.contact ? { id: inv.contact?.id || inv.contact_id, first_name: inv.contact.first_name, last_name: inv.contact.last_name, email: inv.contact.email } : null);
        setLineItems(inv.items && inv.items.length > 0
          ? inv.items.map(li => ({
              description: li.description || '',
              quantity: li.quantity || 1,
              unit_price: li.unit_price || 0,
              amount: li.amount || 0,
            }))
          : [{ ...emptyLineItem }]
        );
        setTaxRate(inv.tax_rate || 0);
        setDiscountAmount(inv.discount_amount || 0);
        setNotes(inv.notes || '');
        setTerms(inv.terms || '');
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      enqueueSnackbar('Failed to load invoice', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Contact search - loads top 5 by default, or searches by query
  const searchContacts = useCallback(async (query) => {
    setContactLoading(true);
    try {
      const params = { per_page: query ? 10 : 5 };
      if (query) params.search = query;
      if (chatbotId) params.chatbot_id = chatbotId;
      const response = await axiosInstance.get('/api/crm/contacts', { params });
      if (response.data.success) {
        setContactOptions(response.data.data || []);
      }
    } catch (err) {
      console.error('Error searching contacts:', err);
    } finally {
      setContactLoading(false);
    }
  }, [chatbotId]);

  // Load top 5 contacts on mount
  useEffect(() => {
    searchContacts('');
  }, [searchContacts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchContacts(contactSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch, searchContacts]);

  // Line items
  const handleLineItemChange = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].amount = (parseFloat(updated[index].quantity) || 0) * (parseFloat(updated[index].unit_price) || 0);
    }
    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { ...emptyLineItem }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // Calculations
  const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const taxAmount = subtotal * ((parseFloat(taxRate) || 0) / 100);
  const discount = parseFloat(discountAmount) || 0;
  const total = subtotal + taxAmount - discount;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const handleSave = async () => {
    if (!selectedContact) {
      enqueueSnackbar('Please select a contact', { variant: 'warning' });
      return;
    }
    if (lineItems.every(li => !li.description.trim())) {
      enqueueSnackbar('Please add at least one line item', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        contact_id: selectedContact.id,
        invoice_number: invoiceNumber || undefined,
        invoice_date: invoiceDate ? invoiceDate.toISOString().split('T')[0] : undefined,
        due_date: dueDate ? dueDate.toISOString().split('T')[0] : undefined,
        items: lineItems.filter(li => li.description.trim()).map(li => ({
          description: li.description,
          quantity: parseFloat(li.quantity) || 1,
          unit_price: parseFloat(li.unit_price) || 0,
          amount: parseFloat(li.amount) || 0,
        })),
        tax_rate: parseFloat(taxRate) || 0,
        discount_amount: parseFloat(discountAmount) || 0,
        notes,
        terms,
        ...(chatbotId && { chatbot_id: chatbotId }),
      };

      let response;
      if (isEdit) {
        response = await axiosInstance.put(`/api/crm/invoices/${invoiceId}`, payload);
      } else {
        response = await axiosInstance.post('/api/crm/invoices', payload);
      }

      if (response.data.success) {
        enqueueSnackbar(isEdit ? 'Invoice updated successfully' : 'Invoice created successfully', { variant: 'success' });
        const newId = response.data.data?.id || invoiceId;
        navigate(`/chatbot/${chatbotId}/crm/salestransaction/invoices/${newId}`);
      } else {
        enqueueSnackbar(response.data.message || 'Failed to save invoice', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error saving invoice:', err);
      enqueueSnackbar('Failed to save invoice', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canModify) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to create or edit invoices.</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, fontFamily }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
              {isEdit ? 'Edit Invoice' : 'New Invoice'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => navigate(-1)}
              sx={{ fontFamily, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              {saving ? 'Saving...' : 'Save Invoice'}
            </Button>
          </Stack>
        </Box>

        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', mb: 3 }}>
          <Grid container spacing={3}>
            {/* Contact Selector */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={contactOptions}
                getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || ''}
                filterOptions={(x) => x}
                value={selectedContact}
                onChange={(e, val) => setSelectedContact(val)}
                onInputChange={(e, val, reason) => {
                  if (reason === 'input') setContactSearch(val);
                }}
                onOpen={() => { if (!contactOptions.length) searchContacts(''); }}
                loading={contactLoading}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography sx={{ fontFamily, fontSize: 14 }}>
                        {`${option.first_name || ''} ${option.last_name || ''}`.trim()}
                      </Typography>
                      {option.email && (
                        <Typography sx={{ fontFamily, fontSize: 12, color: '#6b7280' }}>
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contact *"
                    placeholder="Search contacts..."
                    size="small"
                    sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
                  />
                )}
              />
            </Grid>

            {/* Invoice Number */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Invoice Number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Auto-generated if left empty"
                size="small"
                fullWidth
                sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
              />
            </Grid>

            {/* Dates */}
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Invoice Date"
                value={invoiceDate}
                onChange={(val) => setInvoiceDate(val)}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Due Date"
                value={dueDate}
                onChange={(val) => setDueDate(val)}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }} />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', mb: 3 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily }}>
              Line Items
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addLineItem}
              size="small"
              sx={{ fontFamily, textTransform: 'none', color: '#3b82f6' }}
            >
              Add Item
            </Button>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontFamily, fontWeight: 600, width: '40%' }}>Description</TableCell>
                  <TableCell align="center" sx={{ fontFamily, fontWeight: 600, width: '15%' }}>Quantity</TableCell>
                  <TableCell align="center" sx={{ fontFamily, fontWeight: 600, width: '20%' }}>Unit Price</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 600, width: '20%' }}>Amount</TableCell>
                  <TableCell align="center" sx={{ fontFamily, fontWeight: 600, width: '5%' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                        placeholder="Item description"
                        size="small"
                        fullWidth
                        variant="standard"
                        sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        size="small"
                        variant="standard"
                        inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                        sx={{ fontFamily }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                        size="small"
                        variant="standard"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ fontFamily }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontFamily, fontWeight: 500 }}>
                        {formatCurrency(item.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length <= 1}
                      >
                        <DeleteIcon fontSize="small" sx={{ color: lineItems.length > 1 ? '#ef4444' : '#d1d5db' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Totals & Notes */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
                sx={{ fontFamily, mb: 2, '& .MuiInputBase-root': { fontFamily } }}
              />
              <TextField
                label="Terms & Conditions"
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
                sx={{ fontFamily, '& .MuiInputBase-root': { fontFamily } }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily, mb: 2 }}>
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Tax Rate (%)"
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    size="small"
                    fullWidth
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    sx={{ fontFamily }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Discount"
                    type="number"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ fontFamily }}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily, color: '#6b7280' }}>Subtotal</Typography>
                  <Typography sx={{ fontFamily }}>{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily, color: '#6b7280' }}>Tax ({taxRate}%)</Typography>
                  <Typography sx={{ fontFamily }}>{formatCurrency(taxAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily, color: '#6b7280' }}>Discount</Typography>
                  <Typography sx={{ fontFamily, color: '#ef4444' }}>-{formatCurrency(discount)}</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Total</Typography>
                  <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#3b82f6' }}>
                    {formatCurrency(total)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default InvoiceForm;
